import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import clsx from 'clsx';
import { useContext, useEffect, useState } from 'react';
import { useTextColor } from '../../styles/jss/utils';
import { processImageUrl } from '../../utils/image';
import ResponsiveImage from '../Image/Image';
import Link from '../Link/Link';
import useStyles from './PromoContentItem.styles';
import { SiteContext } from '../../utils/common';
import { useRouter } from 'next/router';
import { useLoaded } from '../MainContent/useComponentLoading';
import SplitContentBySlash from '../../utils/SplitContentBySlash';
import RawHtml from '../RawHtml/RawHtml';
/**
 * Promo Content Item - Design System v2.10.2 - Molecules / Cards
 *
 * * Promo Cards Vertical - m-card_promo_vertical
 */
const getContent = (isFlyingFlowers, text, className) => {
    if (!text || (typeof text === 'string' && !text.trim()))
        return null;
    return !isFlyingFlowers ? _jsx(SplitContentBySlash, { content: text, classNames: { headingLine: className } }) : text;
};
const getImageWidths = (halfWidth) => {
    if (halfWidth) {
        return [240, 384, 480, 960];
    }
    else {
        return [240, 384, 520, 960];
    }
};
const getVariant = (_isFlyingFlowers, _membershipProductSKU) => {
    return 'tertiary';
};
const PromoContentItem = (props) => {
    var _a;
    const { image, link, linkStyle, halfWidth, membershipProductSKU, headingTag, itemIndex, message, backgroundAndTextColours, backgroundColour, } = props; // To make sure we handle any nulls from graphQL
    const { addDeliveryPassToCart, sendPromoContentGalleryAnalytics } = props;
    const [analyticsItem, setAnalyticsItem] = useState();
    const { isFlyingFlowers } = useContext(SiteContext);
    // Always call hooks unconditionally with proper fallbacks
    const { textColor, backgroundColor } = useTextColor((_a = backgroundColour === null || backgroundColour === void 0 ? void 0 : backgroundColour[0]) === null || _a === void 0 ? void 0 : _a.backgroundColour, '#FFFFFF');
    const router = useRouter();
    const sizes = [
        {
            breakpoint: 'sm',
            query: 'max-width',
            width: '100vw',
        },
        {
            breakpoint: 'md',
            query: 'min-width',
            width: halfWidth ? '50w' : '33vw',
        },
    ];
    const imageUrl = image && processImageUrl(image.url, { w: 960, aspect: '16:9', bg: backgroundColor }, ['$poi-square$']);
    const isButton = (linkStyle === null || linkStyle === void 0 ? void 0 : linkStyle.toUpperCase()) === 'BUTTON';
    const HeadingTag = (headingTag || 'h3');
    const { backgroundColour: promoBgColor, textColour: promoHeadingColor } = (backgroundAndTextColours === null || backgroundAndTextColours === void 0 ? void 0 : backgroundAndTextColours[0]) || {};
    // Data for GA4 click event
    useEffect(() => {
        var _a;
        if ((_a = router === null || router === void 0 ? void 0 : router.asPath) === null || _a === void 0 ? void 0 : _a.endsWith('page/world-cup')) {
            const analyticsItem = {
                category: 'FWC23',
                action: 'Sub' + ' ' + (itemIndex || itemIndex === 0 ? itemIndex + 1 : ''),
                label: itemIndex == 2 ? 'Virtual' : 'Hub',
            };
            setAnalyticsItem(analyticsItem);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router === null || router === void 0 ? void 0 : router.asPath]);
    useLoaded(props);
    const onClickHandler = () => {
        if (membershipProductSKU && addDeliveryPassToCart) {
            addDeliveryPassToCart(membershipProductSKU);
        }
        else if (sendPromoContentGalleryAnalytics && link) {
            sendPromoContentGalleryAnalytics(link.label);
        }
    };
    const { classes } = useStyles({
        promoBgColor,
        promoHeadingColor,
    });
    const getTitleClasses = () => {
        if (isFlyingFlowers) {
            return classes.titleFF;
        }
        else {
            return classes.titleUK;
        }
    };
    const getBgColorFF = () => {
        var _a;
        if ((_a = backgroundColour === null || backgroundColour === void 0 ? void 0 : backgroundColour[0]) === null || _a === void 0 ? void 0 : _a.backgroundColour) {
            return backgroundColor;
        }
        else {
            // Return CSS class for predefined backgroundColor (including FlyingFlowers)
            return '';
        }
    };
    const createContainerStyle = (bgColor, textColor) => {
        const hashColor = bgColor === null || bgColor === void 0 ? void 0 : bgColor.startsWith('#');
        return {
            className: clsx(classes.container, hashColor ? '' : bgColor),
            style: {
                backgroundColor: hashColor ? bgColor : undefined,
                color: bgColor ? textColor : undefined,
            },
        };
    };
    const renderImage = (imageUrl, heading, sizes, imageWidths) => {
        if (!image || !imageUrl) {
            return null;
        }
        return (_jsx(ResponsiveImage, { altText: image.altText || heading || '', imageUrl: imageUrl, sizes: sizes, imageWidths: imageWidths, className: classes.image, loading: "lazy" }));
    };
    const renderLink = (link, analyticsItem, isButton, onClickHandler) => {
        if (!(link === null || link === void 0 ? void 0 : link.label)) {
            return null;
        }
        return (_jsx("div", { className: classes.linkContainer, "data-item": analyticsItem ? JSON.stringify(analyticsItem) : undefined, children: _jsx(Link, Object.assign({}, link, { url: !membershipProductSKU ? link.url : undefined,
                button: isButton,
                variant: getVariant(isFlyingFlowers, membershipProductSKU),
                thin: true,
                className: clsx(classes.link, isButton ? classes.linkButton : classes.linkText),
                onClick: membershipProductSKU || sendPromoContentGalleryAnalytics ? onClickHandler : undefined })) }));
    };
    const imageWidths = getImageWidths(halfWidth);
    const titleClasses = getTitleClasses();
    const bgColorFF = getBgColorFF();
    const heading = getContent(isFlyingFlowers, props.heading, classes.headingLine);
    const containerProps = createContainerStyle(bgColorFF, textColor);
    return (_jsxs("div", Object.assign({ "data-testid": "promoContentId" }, containerProps, { children: [_jsx("div", { className: clsx(classes.imageContainer, !imageUrl && classes.noImage), children: renderImage(imageUrl, heading, sizes, imageWidths) }), _jsxs("div", { className: classes.textContent, children: [heading && _jsx(HeadingTag, { className: titleClasses, children: heading }), _jsx(RawHtml, { className: classes.message, content: message }), renderLink(link, analyticsItem, isButton, onClickHandler)] })] })));
};
export default PromoContentItem;
